import { BASE_ELEMENT_IDS } from "../ui/BaseDOM";
import { createElement, paddy } from "../helpers";
import { injectable } from "inversify";
import { getDeviceStr, isMobileLandscape } from "../device/device";
import LoadingScreen from "./LoadingScreen";

@injectable()
export default class LoadingScreenNew extends LoadingScreen {
  protected mainLogoConfig: string;
  protected subLogoConfig: string;
  protected colorsConfig: string[][];

  constructor(config?: {
    mainLogo: string;
    subLogo: string;
    colors: string[][];
  }) {
    super();
    this.mainLogoConfig = config?.mainLogo ?? "logo_new_inverted.svg";
    this.subLogoConfig = config?.subLogo;
    this.colorsConfig = config?.colors;
  }

  protected _wrapper!: HTMLElement;
  protected _mainLogo!: HTMLElement;
  protected _secondaryLogo!: HTMLElement;
  protected _gameLogo!: HTMLElement;
  protected logoWrapper!: HTMLElement;

  // Map of loaderId -> [progress, weight]
  private loaderStates: Map<string, [number, number]> = new Map();

  private shouldUseDarkMode(): boolean {
    const currentHour = new Date().getHours();
    return currentHour >= 19 || currentHour < 7;
  }

  private animationFrameId: number | null = null;
  private isAnimating = false;
  private nextTargetProgress = 0;
  private _loadAssets: any;
  private lastProgress = 0;

  private device: "mobile" | "desktop" | "landscape" =
    getDeviceStr() === "mobile" || getDeviceStr() === "tablet"
      ? isMobileLandscape()
        ? "landscape"
        : "mobile"
      : "desktop";

  private firstUpdateTimeout: number | null = null;
  private isFirstUpdate = true;

  private darkMode = this.shouldUseDarkMode();

  private barConfig = [
    {
      className: "mx-loader-sidebar-bottom",
      position: "right",
      values: {
        desktop: [52.9, 36.1, 18.5, 1.3],
        mobile: [58.5, 39.5, 20.5, 1.3],
        landscape: [52.9, 36.1, 19, 2.3],
      },
      heights: {
        desktop: [10.7, 24.5, 39.2, 51.5],
        landscape: [8.73, 19.97, 31.95, 42.0],
        mobile: [7, 14, 22, 30],
      },
      colors: [
        ["#dbdcdc", "#cecece"],
        ["#bcbcbc", "#939393"],
        ["#959595", "#707070"],
      ],
    },
  ];

  private darkColors = [
    ["#1c1c1c", "#2e2e2e"],
    ["#3a3a3a", "#4f4f4f"],
    ["#5c5c5c", "#737373"],
  ];

  private total = 0;
  private barPercentages: number[] = [];
  private adjustedBreakpoints: number[] = [];

  private animationResolver: () => void = () => {};
  private animationComplete: Promise<void> = new Promise((resolve) => {
    this.animationResolver = resolve;
  });

  /**
   * Creates the loading screen and initializes the logos and progress bars.
   *
   * @param sublogoName - (Optional) Sub-logo image name.
   * @param mainLogoName - (Optional) Main logo image name. Defaults to 'logo_new.svg'.
   * @param colors - (Optional) Array of four color strings for the progress bars.
   */
  public create(): void {
    const loadingElement = document.getElementById(BASE_ELEMENT_IDS.LOADING);
    if (!loadingElement) {
      throw new Error(`Element with id ${BASE_ELEMENT_IDS.LOADING} not found.`);
    }

    loadingElement.classList.add("mx-loading-new");

    if (this.darkMode) {
      this.barConfig[0].colors = this.darkColors;
    }

    if (this.darkMode && this.mainLogoConfig === "logo_new_inverted.svg") {
      this.mainLogoConfig = "logo_new.svg";
    }

    if (this.darkMode) {
      loadingElement.style.backgroundColor = "black";
    } else {
      loadingElement.style.backgroundColor = "white";
    }

    // Set up asset loader
    this._loadAssets = require.context(
      "../../../framework/assets/img/inline/ui",
      false,
      /\.svg$/,
      "lazy"
    );

    // Overwrite default colors if provided
    if (this.colorsConfig && this.colorsConfig.length === 4) {
      this.barConfig[0].colors = this.colorsConfig;
    }

    // Calculate total height and percentages for the side bars
    const heights = this.barConfig[0].heights[this.device];
    this.total = heights.reduce((sum, height) => sum + height, 0);
    this.barPercentages = heights.map(
      (_, index) =>
        heights.slice(0, index + 1).reduce((sum, h) => sum + h, 0) / this.total
    );
    this.adjustedBreakpoints = this.barPercentages.map((percentage, index) =>
      index === this.barPercentages.length - 1 ? 80 : percentage * 100
    );

    this._wrapper = loadingElement;
    this._wrapper.style.overflow = "hidden";

    this.createLogoWrapper();
    this.createMain(undefined, this.mainLogoConfig);
    this.createBar();
    this.createSideBars();

    if (this.subLogoConfig) {
      this.createSubLogo(this.subLogoConfig);
    }
  }

  /**
   * Creates and appends the wrapper for logos.
   */
  private createLogoWrapper(): void {
    this.logoWrapper = createElement("", ["mx-loading-logo-wrapper"]);
    this._wrapper.appendChild(this.logoWrapper);
  }

  /**
   * Creates and appends the game logo.
   */
  public createGameLogo(): void {
    this._gameLogo = createElement("", ["mx-loading-game-logo"]);
    this._wrapper.appendChild(this._gameLogo);
  }

  /**
   * Creates and appends the main logo.
   *
   * @param style - (Optional) Array of additional style class names.
   * @param imageName - Name of the image file.
   */
  public createMain(style?: string[], imageName?: string): void {
    this._mainLogo = createElement(
      "",
      ["mx-loading-logo", ...(style ?? [])],
      "img"
    ) as HTMLImageElement;

    const logoContainer = document.createElement("div");
    logoContainer.className = "logo-container";
    this.logoWrapper.appendChild(logoContainer);

    const logoContainerWrapper = document.createElement("div");
    logoContainerWrapper.className = "logo-container-wrapper";
    logoContainer.appendChild(logoContainerWrapper);

    logoContainerWrapper.appendChild(this._mainLogo);

    this.loadImage(this._mainLogo as HTMLImageElement, imageName!)
      .then(() => {
        this._mainLogo.style.opacity = "1";
      })
      .catch((error) => {
        console.error(`Error loading image ${imageName}:`, error);
      });
  }

  /**
   * Creates and appends a logo image element.
   *
   * @param cls - Array of CSS classes.
   * @param imageName - Image file name.
   * @param animSpeed - (Optional) Logo transition speed in seconds. Defaults to 0.7
   * @returns The created image element.
   */
  public createLogo(
    cls: string[],
    imageName: string,
    animSpeed = 0.7
  ): HTMLImageElement {
    const img = createElement("", cls, "img") as HTMLImageElement;
    img.style.opacity = "0";
    img.style.transition = `opacity ${animSpeed}s`;
    img.onload = () => {
      img.style.opacity = "1";
    };
    img.src = imageName;
    this.logoWrapper.appendChild(img);
    return img;
  }

  /**
   * Loads and appends the sub-logo.
   *
   * @param imageName - Image file name for the sub-logo.
   */
  public createSubLogo(imageName: string): void {
    const cls = ["mx-loading-sublogo"];
    this._loadAssets(`./${imageName}`)
      .then((src: string) => {
        this._secondaryLogo = this.createLogo(cls, src, 1.2);
      })
      .catch((error: Error) => {
        console.error(`Error loading image ${imageName}:`, error);
      });
  }

  /**
   * (Stub) Adds bonus game related functionality.
   *
   * @returns Always returns true.
   */
  public addBonusGame(): boolean {
    return true;
  }

  /**
   * Creates the side bars used for the loading progress.
   */
  public createSideBars(): void {
    const { className, position, values, heights, colors } = this.barConfig[0];
    values[this.device].forEach((positionValue, index) => {
      const container = document.createElement("div");
      if (index === 3) {
        container.className = `${className} special`;
      } else {
        container.className = className;
      }

      container.style[position] = `calc(${positionValue}vw + (${
        heights[this.device][index]
      }vh * tan(12deg))`;
      container.style.height = `${heights[this.device][index]}vh`;

      const innerDiv = document.createElement("div");
      innerDiv.id = `${className}${index + 1}`;
      if (index === 3) {
        innerDiv.className = "mx-inner special";
      } else {
        innerDiv.className = "mx-inner";
      }
      innerDiv.style.height = `100%`;
      innerDiv.style[position] = "-30px";
      innerDiv.style.background = `linear-gradient(90deg, ${colors[index]?.[0]}, ${colors[index]?.[1]})`;

      container.appendChild(innerDiv);
      this._wrapper.appendChild(container);
    });
  }

  /**
   * (Optional) Creates an additional loading bar in the middle.
   *
   * @param style - (Optional) Additional style class.
   */
  public createBar(style?: string): void {
    const cont = createElement(null, ["mx-loading-container-new"]);
    this.logoWrapper.appendChild(cont);

    const cls = ["mx-loading-bar"];
    if (style) {
      cls.push(style);
    }
    const bar = createElement("", cls);
    const gradient = this.barConfig[0].colors;

    const barColor = this.shouldUseDarkMode() ? "white" : "black";
    bar.style.setProperty(
      "--before-background",
      `linear-gradient(90deg, ${barColor}, ${barColor})`
    );
    // bar.style.setProperty(
    //   "--before-background",
    //   `linear-gradient(90deg, ${gradient[0]} 10%, ${gradient[2]})`
    // );
    cont.appendChild(bar);
  }

  /**
   * Starts the loading screen animations.
   */
  public start(): void {
    if (this._gameLogo?.style) this._gameLogo.style.opacity = "1";
  }

  /**
   * Smoothly animates the progress of the loader.
   *
   * @param targetProgress - The progress value to animate to (0 to 100).
   * @param minDuration - (Optional) Minimum animation duration in ms.
   * @param maxDuration - (Optional) Maximum animation duration in ms.
   */
  private smoothProgressLoader(
    targetProgress: number,
    minDuration = 300,
    maxDuration = 1200
  ): void {
    const startProgress = this.lastProgress;

    // If target is 100 but starting progress is very low, enforce default durations.
    if (targetProgress >= 100 && startProgress < 10) {
      minDuration = 1200;
      maxDuration = 2200;
    }

    // Clamp target progress between 0 and 100
    targetProgress = Math.ceil(Math.min(Math.max(targetProgress, 0), 100));

    // Buffer next target progress if already animating (unless finishing)
    if (this.isAnimating && targetProgress < 100) {
      this.nextTargetProgress = targetProgress;
      return;
    }

    // Do not allow progress to go backwards
    if (targetProgress <= startProgress) {
      return;
    }

    const difference = targetProgress - startProgress;
    const duration = Math.min(
      Math.max((difference / 100) * maxDuration, minDuration),
      maxDuration
    );

    const startTime = performance.now();
    this.isAnimating = true;

    // Easing magic
    const easeInOutCubic = (t: number): number =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
    }

    const animate = (currentTime: number): void => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeInOutCubic(progress);

      let currentProgress = startProgress + difference * easedProgress;
      currentProgress = Math.min(currentProgress, targetProgress);
      this.lastProgress = currentProgress;

      // Update the progress bars
      this.fillBarsContinuously(currentProgress);

      if (progress >= 1) {
        this.isAnimating = false;
        if (currentProgress >= 100) {
          setTimeout(() => {
            this.animationResolver();
          }, 400);
        }
        // If there is buffered progress, animate that next.
        if (this.nextTargetProgress > targetProgress) {
          const bufferedProgress = this.nextTargetProgress;
          this.nextTargetProgress = 0;
          this.smoothProgressLoader(bufferedProgress);
        }
      } else {
        this.animationFrameId = requestAnimationFrame(animate);
      }
    };

    this.animationFrameId = requestAnimationFrame(animate);
  }

  /**
   * Updates the side bars in discrete steps.
   *
   * @param newProgress - The new progress percentage.
   */
  public fillBarsDiscretely(newProgress: number): void {
    this.adjustedBreakpoints.forEach((breakpoint, index) => {
      const bar = document.getElementById(
        `mx-loader-sidebar-bottom${index + 1}`
      );
      if (bar) {
        bar.style.height = newProgress >= breakpoint ? "100%" : "0%";
      }
    });
  }

  /**
   * Updates the side bars continuously based on progress.
   *
   * @param newProgress - The new progress percentage.
   */
  public fillBarsContinuously(newProgress: number): void {
    this.barPercentages.forEach((percentage, index) => {
      // const currentPercentage = percentage * 100;
      // const previousPercentage =
      //   index === 0 ? 0 : this.barPercentages[index - 1] * 100;
      // const bar = document.getElementById(
      //   `mx-loader-sidebar-bottom${index + 1}`
      // );
      const loadingBar = document.getElementsByClassName("mx-loading-bar")[0];
      if (loadingBar) {
        loadingBar.style.width = `${newProgress}%`;
      }

      // if (bar) {
      //   if (newProgress >= currentPercentage) {
      //     bar.style.height = "100%";
      //   } else if (newProgress > previousPercentage) {
      //     const barRange = currentPercentage - previousPercentage;
      //     const progressInBar = newProgress - previousPercentage;
      //     const barHeight = (progressInBar / barRange) * 100;
      //     bar.style.height = `${barHeight}%`;
      //   } else {
      //     bar.style.height = "0%";
      //   }
      // }
    });
  }

  /**
   * Updates the overall progress based on multiple loaders with different weights.
   *
   * @param loaderId - Unique identifier for the loader.
   * @param progress - Progress value for this loader.
   * @param weight - Progress value weight in total progress.
   */
  public updateMultiple(
    loaderId: string,
    progress: number,
    weight: number
  ): void {
    // Do not allow a loader's progress to go backwards
    const currentLoader = this.loaderStates.get(loaderId);
    if (currentLoader && currentLoader[0] > progress) {
      return;
    }
    this.loaderStates.set(loaderId, [progress, weight]);

    const totalWeight = Array.from(this.loaderStates.values()).reduce(
      (acc, [, w]) => acc + w,
      0
    );

    // No float math in my christian app :(
    const scaleFactor = 1e6;
    // Calculate total progress based on weights
    const totalProgress =
      Math.round(
        Array.from(this.loaderStates.values()).reduce(
          (acc, [value, w]) => acc + value * (w / totalWeight),
          0
        ) * scaleFactor
      ) / scaleFactor;

    this.update(totalProgress);
  }

  /**
   * Updates the loading progress. Use this method if only one loader is present.
   *
   * @param perc - The progress percentage.
   */
  public update(perc: number): void {
    if (this.isFirstUpdate) {
      if (this.firstUpdateTimeout) {
        clearTimeout(this.firstUpdateTimeout);
      }
      this.firstUpdateTimeout = window.setTimeout(() => {
        this.smoothProgressLoader(perc);
        this.isFirstUpdate = false;
      }, 120);
    } else {
      if (this.firstUpdateTimeout) {
        clearTimeout(this.firstUpdateTimeout);
      }
      this.smoothProgressLoader(perc);
    }
  }

  /**
   * Hides the loading screen after the animation is complete.
   *
   * @returns A Promise that resolves when the loader is hidden,
   *          or false if an "infinite" URL parameter is present.
   */
  public hide(): false | Promise<void> {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("infinite")) {
      return false;
    }
    return new Promise<void>((resolve) => {
      this.animationComplete.then(() => {
        this._wrapper.style.opacity = "0";
        setTimeout(() => {
          this._wrapper.style.display = "none";
          resolve();
        }, 400);
      });
    });
  }

  /**
   * Loads an image using the configured asset loader.
   *
   * @param el - The image element to set the source on.
   * @param imageName - The name of the image file.
   */
  private async loadImage(
    el: HTMLImageElement,
    imageName: string
  ): Promise<void> {
    try {
      const module = await this._loadAssets(`./${imageName}`);
      el.src = module;
    } catch (error) {
      console.error(`Error loading image ${imageName}:`, error);
      throw error;
    }
  }
}
